'use client';

import { getAccountViewAsTarget, type AccountViewAsTarget } from '@/@subsidiary/accountViewAs';
import { useSubsidiaryFilterOptional } from '@/@subsidiary/context';
import { useMemo } from 'react';

/**
 * Stable View-as account target. Returns the same object reference when
 * orgId / sfAccountId / orgName are unchanged (avoids effect/query thrash).
 */
export function useAccountViewAsTarget(): AccountViewAsTarget | null {
    const filter = useSubsidiaryFilterOptional();

    const isInternal = filter?.isInternal === true;
    const masqueradeOrgId = filter?.masqueradeOrgId ?? null;
    const selectedOrgIds = filter?.selectedOrgIds;
    const selectedSingle = selectedOrgIds?.length === 1 ? (selectedOrgIds[0] ?? null) : null;
    const targetOrgId =
        isInternal && masqueradeOrgId != null ? (selectedSingle ?? masqueradeOrgId) : null;

    const sfAccountId =
        targetOrgId == null
            ? null
            : (filter?.hierarchyOptions?.find((o) => o.orgId === targetOrgId)?.sfAccountId ??
              filter?.options?.find((o) => o.orgId === targetOrgId)?.sfAccountId ??
              null);

    const orgName =
        targetOrgId == null
            ? null
            : (filter?.hierarchyOptions?.find((o) => o.orgId === targetOrgId)?.orgName ??
              filter?.options?.find((o) => o.orgId === targetOrgId)?.orgName ??
              null);

    return useMemo(() => {
        if (targetOrgId == null || !sfAccountId || !orgName) return null;
        return { orgId: targetOrgId, sfAccountId, orgName };
    }, [targetOrgId, sfAccountId, orgName]);
}

/** @deprecated Prefer useAccountViewAsTarget — kept for non-hook call sites */
export { getAccountViewAsTarget };
